import { useState, useRef, useEffect } from 'react'
import { ChevronDown, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * Merke multi-select for the Alder-profil and Kjøper-alder tabs (#482, #485)
 * and the Perioder > Graf tab (#685).
 *
 * Selection is capped at `max` so the chart cannot be handed more lines than it
 * can render — the backend applies the same cap, and silently dropping the
 * extras in the UI would leave the user wondering where their brand went.
 */

interface Props {
  options: string[]
  selected: string[]
  onChange: (next: string[]) => void
  max?: number
  label?: string
}

export default function MerkeMultiSelect({
  options,
  selected,
  onChange,
  max = 8,
  label,
}: Props) {
  const { t } = useTranslation('bruktbil')
  const shownLabel = label ?? t('merkeMultiSelect.label')
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const atMax = selected.length >= max
  const shown = options.filter(o => o.toLowerCase().includes(q.trim().toLowerCase()))

  function toggle(merke: string) {
    if (selected.includes(merke)) onChange(selected.filter(m => m !== merke))
    else if (!atMax) onChange([...selected, merke])
  }

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          data-testid="merke-multiselect"
          className="btn-secondary text-sm inline-flex items-center gap-1"
        >
          {shownLabel}: {selected.length ? t('merkeMultiSelect.nValgt', { n: selected.length }) : t('merkeMultiSelect.alle')}
          <ChevronDown className="w-4 h-4" />
        </button>

        {selected.map(m => (
          <span
            key={m}
            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-sm bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
          >
            {m}
            <button
              type="button"
              aria-label={t('merkeMultiSelect.fjern', { name: m })}
              onClick={() => onChange(selected.filter(x => x !== m))}
              className="hover:text-primary-900 dark:hover:text-primary-100"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}

        {selected.length > 0 && (
          <button type="button" onClick={() => onChange([])} className="text-xs text-gray-500 hover:underline">
            {t('merkeMultiSelect.nullstill')}
          </button>
        )}
      </div>

      {open && (
        <div
          data-testid="merke-options"
          role="listbox"
          aria-label={t('merkeMultiSelect.label')}
          className="absolute z-20 mt-1 w-72 max-h-80 overflow-y-auto rounded-sm border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-lg"
        >
          <div className="p-2 sticky top-0 bg-white dark:bg-dark-card border-b border-gray-100 dark:border-dark-border">
            <input
              type="text"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder={t('merkeMultiSelect.sokPlaceholder')}
              aria-label={t('merkeMultiSelect.sokAria')}
              className="w-full px-2 py-1 text-sm rounded-sm border border-gray-200 dark:border-dark-border bg-transparent"
            />
            {atMax && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                {t('merkeMultiSelect.maks', { max })}
              </p>
            )}
          </div>
          {shown.length === 0 && (
            <p className="px-3 py-2 text-sm text-gray-500">{t('merkeMultiSelect.ingenTreff')}</p>
          )}
          {shown.map(o => {
            const checked = selected.includes(o)
            return (
              <label
                key={o}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                  !checked && atMax ? 'opacity-40 cursor-not-allowed' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!checked && atMax}
                  onChange={() => toggle(o)}
                />
                {o}
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}
